/**
 * The shared list of templates a user can pick from.
 *
 * Feeds three UIs that render it differently — the Chakra selects in
 * Add/Edit Patient and the Tailwind dialog in the clinic — so this module
 * deliberately exposes DATA, not a component. Tailwind utilities only apply
 * inside `#clinic-root`, so a single shared component would need a variant
 * prop and buy nothing.
 *
 * See docs/superpowers/specs/2026-08-07-template-field-and-reload-picker-design.md
 */
import { templates } from '../../assets/templates';
import { listCustomTemplates } from '../../hooks/medstudent/customTemplates';
import { withTimeout } from './priorDocPrompt';

// FFP/FFS are not template files — they mean "copy the most recent prior visit
// with a populated document" (usePatientDocument.js FOLLOW_UP_TYPES, mirrored
// in backend/api/collab/seed.py). The label carries that, because it is the
// only place a user can learn these two behave differently from the rest.
export const FOLLOW_UP_OPTIONS = [
  { code: 'FFP', label: 'FFP — copy previous visit', source: 'followup' },
  { code: 'FFS', label: 'FFS — copy previous visit', source: 'followup' },
];

const norm = (c) => String(c || '').trim().toUpperCase();

/**
 * Marker that pins a code to the BUILT-IN template, used only when a custom
 * template shares the same acronym.
 *
 * The `notes-c.template` column stores a single string, so a bare "NS" cannot
 * say which source you picked — and resolveTemplateDoc tries storage (custom)
 * first. Prefixing only the built-in keeps a bare code meaning exactly what it
 * has always meant (custom-preferred), so existing rows and the "type a custom
 * code into the Type field" workflow are unaffected.
 */
export const BUILTIN_PREFIX = 'BUILTIN:';

export function isBuiltinPinned(code) {
  return norm(code).startsWith(BUILTIN_PREFIX);
}

/** The bare template code, with any BUILTIN: pin removed. */
export function stripBuiltinPrefix(code) {
  const c = norm(code);
  return c.startsWith(BUILTIN_PREFIX) ? c.slice(BUILTIN_PREFIX.length) : c;
}

/**
 * Built-in template codes: the keys of `templates`, minus the `MEDSTUDENT_*`
 * variants. Those are chosen automatically by buildInitialDocFromTemplate for
 * a matching type; exposing them separately would present one template twice.
 */
export function builtinTemplateCodes() {
  return Object.keys(templates)
    .filter((k) => !k.startsWith('MEDSTUDENT_'))
    .sort();
}

export function mergeTemplateOptions(customCodes = [], { includeFollowUps = false } = {}) {
  const custom = [...new Set((customCodes || []).map(norm).filter(Boolean))].sort();
  const customSet = new Set(custom);

  // Both are offered when a custom shares a built-in's acronym. They need
  // DIFFERENT option values or the select cannot tell them apart — so the
  // built-in gets pinned with BUILTIN: while the custom keeps the bare code
  // (which already resolves custom-first).
  const options = builtinTemplateCodes().map((code) => (
    customSet.has(code)
      ? { code: `${BUILTIN_PREFIX}${code}`, label: `${code} (system)`, source: 'builtin' }
      : { code, label: code, source: 'builtin' }
  ));

  options.push(...custom.map((code) => ({ code, label: `${code} (custom)`, source: 'custom' })));

  if (includeFollowUps) options.push(...FOLLOW_UP_OPTIONS);
  return options;
}

/**
 * Built-ins are synchronous; only the custom list needs I/O. That call is
 * timeout-guarded because it reaches supabase.auth.getSession(), which can
 * deadlock when multiple GoTrueClient instances share a storage key — as they
 * do here. A hung lookup must degrade to "built-ins only", never to a hung UI.
 */
export async function listTemplateOptions({ includeFollowUps = false } = {}) {
  let customCodes = [];
  try {
    customCodes = (await withTimeout(listCustomTemplates(), 4000, [])) || [];
  } catch (_) {
    customCodes = [];
  }
  return mergeTemplateOptions(customCodes, { includeFollowUps });
}

/**
 * Keep a saved code selectable even when its template no longer exists (custom
 * template deleted or renamed). Without this the select would silently blank
 * the value, so opening and saving an unrelated field would erase it.
 */
export function withStaleOption(options, currentCode) {
  const code = norm(currentCode);
  if (!code) return options;
  if (options.some((o) => o.code === code)) return options;
  return [...options, { code, label: `${code} (not found)`, source: 'stale' }];
}
